import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { threatAnalyticsAxios } from "@phc/common/gen/threatAnalyticsApiMutator"
import type {
  GdeltClusterMemberArticle,
  GdeltClusterMemberArticleList,
} from "./gdeltClusterMemberTypes"

/**
 * The `/gdelt/signal-clusters/{id}/members` route returns the cluster's member
 * articles already deduplicated by title (oldest article per title kept) and
 * sorted by recency, with `total_count` (raw article count) alongside the
 * deduped `count`. Deduplication is owned by the backend — the drawer just
 * displays what the API returns.
 */

export interface GdeltClusterSourceSummary {
  name: string
  count: number
  homepage: string | null
}

export interface GdeltClusterMembersResult {
  members: GdeltClusterMemberArticle[]
  /** Unique headline count (post-dedup). */
  uniqueCount: number
  /** Raw article count (pre-dedup). */
  totalCount: number
  /** Distinct source/outlet count across the members. */
  uniqueSourceCount: number
  sources: GdeltClusterSourceSummary[]
  isLoading: boolean
  isError: boolean
  /** When true, members are deduped by headline (API `dedup=true`). */
  showUniqueOnly: boolean
}

export const useGdeltClusterMembers = (
  clusterId: string | null,
  showUniqueOnly = true
): GdeltClusterMembersResult => {
  const dedup = showUniqueOnly
  const clusterKey = clusterId ?? ""
  const query = useQuery({
    queryKey: [
      `/gdelt/signal-clusters/${clusterKey}/members`,
      { dedup },
    ] as const,
    enabled: Boolean(clusterId),
    queryFn: async ({ signal }) => {
      const { data } =
        await threatAnalyticsAxios.get<GdeltClusterMemberArticleList>(
          `/gdelt/signal-clusters/${clusterKey}/members`,
          { params: { dedup }, signal }
        )
      return data
    },
  })

  const data: GdeltClusterMemberArticleList | undefined = query.data
  const members = useMemo((): GdeltClusterMemberArticle[] => {
    if (!data) return []
    return data.results
  }, [data])
  const uniqueCount = data != null ? data.count : members.length
  const totalCount = data != null ? data.total_count : uniqueCount

  const uniqueSourceCount = useMemo(
    () =>
      new Set(members.map(member => member.source_name).filter(Boolean)).size,
    [members]
  )

  // Member counts grouped by source/outlet, most prolific first. Each source
  // keeps the homepage origin of the first member URL we can parse.
  const sources = useMemo(() => {
    const map = new Map<string, { count: number; homepage: string | null }>()
    for (const member of members) {
      const name = member.source_name?.trim() || "Unknown"
      const existing = map.get(name)
      map.set(name, {
        count: (existing?.count ?? 0) + 1,
        homepage: existing?.homepage ?? homepageFromUrl(member.url),
      })
    }
    return [...map.entries()]
      .map(([name, value]) => ({ name, ...value }))
      .sort((a, b) => b.count - a.count)
  }, [members])

  return {
    members,
    uniqueCount,
    totalCount,
    uniqueSourceCount,
    sources,
    isLoading: query.isLoading,
    isError: query.isError,
    showUniqueOnly,
  }
}

/** A news site's homepage = the origin of one of its article URLs. */
const homepageFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null
  try {
    return new URL(url).origin
  } catch {
    return null
  }
}
